import type { FunctionComponent, ReactNode } from 'react';

import { useParams } from 'react-router';

import type { UseAsyncDataResT } from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import { AdminPage } from 'components/admin';

import { useEditedVideo } from 'hooks';
import useUserArtist from 'hooks/useUserArtist';

import {
  type ArtistT,
  type UserT,
  type VideoT,
  useGlobalState,
} from 'utils';

import Editor from './Editor';

import S from './style.scss';

type ModeNameT = 'Add New' | 'Edit';

type StatusT
  = | 'BUSY'
    | 'FORBIDDEN_TO_ADD_NEW'
    | 'FORBIDDEN_TO_EDIT'
    | 'OK'
    | 'VIDEO_NOT_FOUND';

/**
 * Given the page mode (add new video, or edit an existing one), the global
 * state envelope for the video being added / edited, the user object, and
 * the envelope for the user's artist record (if any), it deduces the high-
 * level page state: busy loading data, one of graceful error messages, or
 * "OK" to proceed rendering the main page content.
 */
function getStatus(
  isAddNewMode: boolean,
  video: UseAsyncDataResT<VideoT>,
  user: UserT,
  userArtist: UseAsyncDataResT<ArtistT>,
): StatusT {
  if (isAddNewMode) {
    if (!user.permissions.VIDEO_UPLOAD) return 'FORBIDDEN_TO_ADD_NEW';
    if (userArtist.loading) return 'BUSY';
    if (userArtist.data?.status !== 'Active') return 'FORBIDDEN_TO_ADD_NEW';
    return 'OK';
  }

  if (video.loading) return 'BUSY';
  if (!video.data) return 'VIDEO_NOT_FOUND';

  if (!user.permissions.EDIT_ANY_VIDEO_DETAILS) {
    if (!user.permissions.VIDEO_UPLOAD) return 'FORBIDDEN_TO_EDIT';
    if (userArtist.loading) return 'BUSY';

    if (
      userArtist.data?.status !== 'Active'
      || userArtist.data.id !== video.data.artistId
    ) return 'FORBIDDEN_TO_EDIT';
  }

  return 'OK';
}

const AdminEditVideoPage: FunctionComponent = () => {
  const [user] = useGlobalState('user');
  if (!user?.id) throw Error('Unauthorized');

  const { idOrAddNew } = useParams();
  if (!idOrAddNew) throw Error('Internal error');

  const isAddNewMode = idOrAddNew === 'add-new';
  const modeName: ModeNameT = isAddNewMode ? 'Add New' : 'Edit';

  const video = useEditedVideo(isAddNewMode ? null : idOrAddNew);
  const userArtist = useUserArtist();

  let content: ReactNode;

  switch (getStatus(isAddNewMode, video, user, userArtist)) {
    case 'BUSY':
      content = <Throbber withOverlay />;
      break;
    case 'FORBIDDEN_TO_ADD_NEW':
      content = (
        <div styleName="centered">
          Not permitted to add new videos
        </div>
      );
      break;
    case 'FORBIDDEN_TO_EDIT':
      content = (
        <div styleName="centered">
          Not permitted to edit this video
        </div>
      );
      break;
    case 'OK':
      content = <Editor userArtist={userArtist} video={video} />;
      break;
    case 'VIDEO_NOT_FOUND':
      content = <div styleName="centered">Video not found</div>;
      break;
    default: throw Error('Unexpected case');
  }

  return (
    <AdminPage
      contentClassName={S.container}
      subTitle={`Content Management / ${modeName} Video`}
      title={`${modeName} Video`}
    >
      {content}
    </AdminPage>
  );
};

export default AdminEditVideoPage;
